import { useState, useEffect, useRef } from "react";

// ── Constants & helpers ───────────────────────────────────────
const C = {
  bg:"#03080f", panel:"#080f1a", border:"#0e2035",
  text:"#cbd5e1", muted:"#3a5068", bright:"#e2e8f0",
};
const mono = { fontFamily:"monospace" };
const f = (n,d=2) => Number(n).toFixed(d);

// ── Physics ───────────────────────────────────────────────────
function physics(satMass, alt, ispEP, years=5) {
  const g0=9.81, G=3.986e14, RE=6371e3;
  const rho = 4.71e-13 * Math.exp((500-alt)/48);
  const v   = Math.sqrt(G/(RE+alt*1000));
  const area = satMass<=14?0.03:satMass<=50?0.07:0.12;
  const Fd  = 0.5*2.2*area*rho*v*v;
  const T   = 2*Math.PI*(RE+alt*1000)/v;
  const dvIn = Fd*3.156e7*years/satMass;
  const dvNS = 48.6*years;
  const dvT  = dvIn+dvNS;
  const veEP  = ispEP*g0, veIon=1500*g0;
  const prop_ep  = satMass*(dvT/veEP)*1.020*0.90*0.92;
  const prop_ion = satMass*(dvT/veIon)*1.058;
  const ionPow   = satMass<=30?150:satMass<=100?250:350;
  const panelMass= ionPow/12;
  const batMass  = ionPow*35*60/3600/180;
  const ep={
    propellant:prop_ep, tank:0.15+0.008*satMass,
    thruster:ispEP>=300?0.6+0.002*satMass:0.12+0.001*satMass,
    emms:Math.min(0.5+0.012*satMass,4.5), emmsCtrl:0.12+0.001*satMass,
    solar:0.02, battery:0.02, pcu:0.06+0.001*satMass, rw:0,
  };
  ep.total=Object.values(ep).reduce((a,b)=>a+b,0);
  const ion={
    propellant:prop_ion, tank:0.08+0.002*satMass,
    thruster:0.35+0.004*satMass, ppu:0.45+0.005*satMass,
    xenonFeed:0.18+0.002*satMass, solar:panelMass,
    battery:batMass, pcu:0.30+0.003*satMass,
    rw:0.40+0.004*satMass, rwCtrl:0.12+0.001*satMass,
  };
  ion.total=Object.values(ion).reduce((a,b)=>a+b,0);
  return { ep, ion, Fd, v, T, rho, dvT, dvIn, dvNS, prop_ep, prop_ion, ionPow, alt, satMass, ispEP };
}

// ── UI atoms ──────────────────────────────────────────────────
const SL = ({children,color=C.muted}) => (
  <div style={{...mono,fontSize:9,color,letterSpacing:3,textTransform:"uppercase",marginBottom:10}}>{children}</div>
);

// ── ORBIT DRIFT VISUALISATION ─────────────────────────────────
function OrbitDriftViz({ D }) {
  const [tick, setTick] = useState(0);
  const [running, setRunning] = useState(false);
  const raf = useRef(null);
  const tRef = useRef(0);

  useEffect(() => {
    if (running) {
      const step = () => {
        tRef.current += 0.4;
        setTick(tRef.current);
        raf.current = requestAnimationFrame(step);
      };
      raf.current = requestAnimationFrame(step);
    } else cancelAnimationFrame(raf.current);
    return () => cancelAnimationFrame(raf.current);
  }, [running]);

  const W=560, H=200, CY=H/2, orbitR=70, cx=W*0.18;
  const t = tick * 0.018;
  const T = D.T;
  const ionCycle = 900;

  const epDrift = (t%133)/133 * (D.Fd*133/D.satMass) * 180;
  const epAngle = -Math.PI/2 + t*(2*Math.PI/T) - epDrift*0.0003;
  const epX = cx + orbitR*Math.cos(epAngle);
  const epY = CY + orbitR*Math.sin(epAngle);

  const ionDrift = (t%ionCycle)/ionCycle * (D.Fd*ionCycle/D.satMass) * 180;
  const ionAngle = -Math.PI/2 + t*(2*Math.PI/T) - ionDrift*0.0003;
  const ionX = cx + (orbitR+8)*Math.cos(ionAngle);
  const ionY = CY + (orbitR+8)*Math.sin(ionAngle);

  const refAngle = -Math.PI/2 + t*(2*Math.PI/T);
  const refX = cx + orbitR*Math.cos(refAngle);
  const refY = CY + orbitR*Math.sin(refAngle);

  const boxX = W*0.45, boxW = W*0.52, boxH = H-30;
  const maxErr = 200;
  const pxPerM = (boxH*0.4)/maxErr;

  const epErr  = ((t%133)/133) * (D.Fd*133/D.satMass);
  const ionErr = ((t%ionCycle)/ionCycle) * (D.Fd*ionCycle/D.satMass);

  const hist = 60;
  const epHist  = Array.from({length:hist},(_,i) => {
    const tt = t - i*0.4;
    return ((tt%133)/133)*(D.Fd*133/D.satMass);
  });
  const ionHist = Array.from({length:hist},(_,i) => {
    const tt = t - i*0.4;
    return ((tt%ionCycle)/ionCycle)*(D.Fd*ionCycle/D.satMass);
  });

  return (
    <div style={{background:"#030810",borderRadius:8,padding:14,marginBottom:14}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <SL color="#0e9cc0">Live Station Keeping Simulation</SL>
        <div style={{display:"flex",gap:6}}>
          <button onClick={()=>setRunning(r=>!r)} style={{
            padding:"4px 12px",...mono,fontSize:9,cursor:"pointer",
            background:running?"#ef444420":"#22c55e20",
            border:`1px solid ${running?"#ef444455":"#22c55e55"}`,
            color:running?"#ef4444":"#22c55e",borderRadius:3}}>
            {running?"■ STOP":"▶ PLAY"}</button>
          <button onClick={()=>{tRef.current=0;setTick(0);setRunning(false)}} style={{
            padding:"4px 12px",...mono,fontSize:9,cursor:"pointer",
            background:"transparent",border:`1px solid ${C.border}`,
            color:C.muted,borderRadius:3}}>RESET</button>
        </div>
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{display:"block",overflow:"visible"}}>
        <circle cx={cx} cy={CY} r={28} fill="#0d2137" stroke="#0e2035" strokeWidth={1}/>
        <circle cx={cx} cy={CY} r={28} fill="url(#earthGrad)" opacity={0.7}/>
        <defs>
          <radialGradient id="earthGrad" cx="40%" cy="35%">
            <stop offset="0%" stopColor="#1a4a8a" stopOpacity="0.8"/>
            <stop offset="100%" stopColor="#0a1520" stopOpacity="0.3"/>
          </radialGradient>
        </defs>
        <text x={cx} y={CY+4} textAnchor="middle" fill="#3a5068" fontSize={7} fontFamily="monospace">EARTH</text>
        <circle cx={cx} cy={CY} r={orbitR} fill="none" stroke="#0e2035" strokeWidth={1} strokeDasharray="4 3"/>
        <circle cx={cx} cy={CY} r={orbitR+8} fill="none" stroke="#0e203566" strokeWidth={1} strokeDasharray="3 4"/>
        <circle cx={refX} cy={refY} r={4} fill="#1e3a52"/>
        <circle cx={ionX} cy={ionY} r={5} fill="#a855f7" opacity={0.9}/>
        <rect x={ionX-7} y={ionY-2} width={5} height={4} fill="#6d28d9" opacity={0.6}/>
        <rect x={ionX+2} y={ionY-2} width={5} height={4} fill="#6d28d9" opacity={0.6}/>
        <circle cx={epX} cy={epY} r={5} fill="#22c55e" opacity={0.9}/>
        <rect x={epX-7} y={epY-2} width={5} height={4} fill="#16a34a" opacity={0.6}/>
        <rect x={epX+2} y={epY-2} width={5} height={4} fill="#16a34a" opacity={0.6}/>
        <text x={ionX+8} y={ionY-4} fill="#a855f7" fontSize={8} fontFamily="monospace">ION</text>
        <text x={epX+8} y={epY+10} fill="#22c55e" fontSize={8} fontFamily="monospace">EP-ADCS</text>
        <text x={cx} y={H-8} textAnchor="middle" fill={C.muted} fontSize={7} fontFamily="monospace">
          {D.alt}km LEO orbit
        </text>
        <rect x={boxX} y={15} width={boxW} height={boxH} fill="#0a1520" stroke="#0e2035" strokeWidth={1} rx={4}/>
        <text x={boxX+boxW/2} y={10} textAnchor="middle" fill={C.muted} fontSize={8} fontFamily="monospace">
          Position Error from Reference Orbit (metres)
        </text>
        <line x1={boxX} y1={15+boxH*0.5} x2={boxX+boxW} y2={15+boxH*0.5} stroke="#0e2035" strokeWidth={1}/>
        <text x={boxX-2} y={15+boxH*0.5+3} fill={C.muted} fontSize={7} fontFamily="monospace" textAnchor="end">0m</text>
        {[50,100,150].map(m=>(
          <g key={m}>
            <line x1={boxX} y1={15+boxH*0.5+m*pxPerM} x2={boxX+boxW} y2={15+boxH*0.5+m*pxPerM}
              stroke="#0e203544" strokeWidth={0.5}/>
            <text x={boxX-2} y={15+boxH*0.5+m*pxPerM+3} fill="#1e3a52" fontSize={6} fontFamily="monospace" textAnchor="end">{m}m</text>
          </g>
        ))}
        <polyline
          points={epHist.map((e,i)=>`${boxX+boxW-i*(boxW/hist)},${15+boxH*0.5+Math.min(e,maxErr)*pxPerM}`).join(" ")}
          fill="none" stroke="#22c55e" strokeWidth={1.5} opacity={0.8}/>
        <polyline
          points={ionHist.map((e,i)=>`${boxX+boxW-i*(boxW/hist)},${15+boxH*0.5+Math.min(e,maxErr)*pxPerM}`).join(" ")}
          fill="none" stroke="#a855f7" strokeWidth={1.5} opacity={0.8}/>
        <circle cx={boxX+boxW} cy={15+boxH*0.5+Math.min(epErr,maxErr)*pxPerM} r={4} fill="#22c55e"/>
        <circle cx={boxX+boxW} cy={15+boxH*0.5+Math.min(ionErr,maxErr)*pxPerM} r={4} fill="#a855f7"/>
        <text x={boxX+boxW-4} y={15+boxH*0.5+Math.min(epErr,maxErr)*pxPerM-6}
          fill="#22c55e" fontSize={8} fontFamily="monospace" textAnchor="end">
          EP: {f(Math.min(epErr,9999),1)}m</text>
        <text x={boxX+boxW-4} y={15+boxH*0.5+Math.min(ionErr,maxErr)*pxPerM+14}
          fill="#a855f7" fontSize={8} fontFamily="monospace" textAnchor="end">
          ION: {f(Math.min(ionErr,9999),1)}m</text>
        {t%133 < 1.5 && (
          <circle cx={epX} cy={epY} r={10} fill="none" stroke="#22c55e" strokeWidth={1.5} opacity={0.6}>
            <animate attributeName="r" values="5;14" dur="0.5s"/>
            <animate attributeName="opacity" values="0.8;0" dur="0.5s"/>
          </circle>
        )}
        {t%ionCycle < 3 && (
          <circle cx={ionX} cy={ionY} r={14} fill="none" stroke="#a855f7" strokeWidth={2} opacity={0.5}/>
        )}
      </svg>
      <div style={{display:"flex",gap:16,marginTop:8,justifyContent:"center"}}>
        {[
          {c:"#22c55e",l:`EP-ADCS: corrects every 133s, max drift ~${f(D.Fd*133/D.satMass,1)}m`},
          {c:"#a855f7",l:`Ion: corrects every 15min (warm-up), max drift ~${f(D.Fd*900/D.satMass,1)}m`},
        ].map(i=>(
          <div key={i.l} style={{display:"flex",alignItems:"center",gap:6}}>
            <div style={{width:12,height:3,background:i.c,borderRadius:2}}/>
            <span style={{...mono,fontSize:9,color:C.muted}}>{i.l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── JITTER VISUALISATION ──────────────────────────────────────
function JitterViz() {
  const W=500, H=180;
  const points = 300;
  const t = useRef(0);
  const [frame, setFrame] = useState(0);
  const [running, setRunning] = useState(false);
  const raf = useRef(null);

  useEffect(() => {
    if (running) {
      const step = () => { t.current+=0.5; setFrame(f=>f+1); raf.current=requestAnimationFrame(step); };
      raf.current = requestAnimationFrame(step);
    } else cancelAnimationFrame(raf.current);
    return () => cancelAnimationFrame(raf.current);
  }, [running]);

  const genTrace = (amp, freqs) =>
    Array.from({length:points},(_,i) => {
      const x = i/points;
      return freqs.reduce((s,{f:freq,a})=>s+a*Math.sin(2*Math.PI*freq*x+t.current*freq*0.01),0) * amp;
    });

  const rwTrace = genTrace(1, [{f:12,a:0.4},{f:35,a:0.3},{f:8,a:0.2},{f:60,a:0.1}]);
  const epTrace = genTrace(0.006, [{f:2,a:0.5},{f:0.5,a:0.5}]);

  const CY_rw = H*0.28, CY_ep = H*0.75;
  const scaleRW = (H*0.22)/800;

  const toPath = (trace, cy, scaleY) => {
    const pw = (W*0.72)/points;
    return trace.map((v,i)=>`${i===0?"M":"L"} ${W*0.05+i*pw} ${cy+v*scaleY}`).join(" ");
  };

  return (
    <div style={{background:"#030810",borderRadius:8,padding:14,marginBottom:14}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <SL color="#6366f1">Pointing Jitter — Real-Time Waveform</SL>
        <button onClick={()=>setRunning(r=>!r)} style={{
          padding:"4px 12px",...mono,fontSize:9,cursor:"pointer",
          background:running?"#ef444420":"#22c55e20",
          border:`1px solid ${running?"#ef444455":"#22c55e55"}`,
          color:running?"#ef4444":"#22c55e",borderRadius:3}}>
          {running?"■ STOP":"▶ ANIMATE"}</button>
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`}>
        <text x={W*0.02} y={CY_rw-28} fill="#a855f7" fontSize={9} fontFamily="monospace">
          REACTION WHEEL (with ion)  —  σ = 800 μrad</text>
        <text x={W*0.02} y={CY_ep-28} fill="#22c55e" fontSize={9} fontFamily="monospace">
          EP-ADCS EMMS  —  σ = 5 μrad  (160× quieter)</text>
        <line x1={W*0.05} y1={CY_rw} x2={W*0.97} y2={CY_rw} stroke="#0e2035" strokeWidth={0.5}/>
        <line x1={W*0.05} y1={CY_ep} x2={W*0.97} y2={CY_ep} stroke="#0e2035" strokeWidth={0.5}/>
        <line x1={W*0.02} y1={CY_rw-800*scaleRW} x2={W*0.02} y2={CY_rw+800*scaleRW}
          stroke="#a855f733" strokeWidth={3}/>
        <text x={0} y={CY_rw-800*scaleRW} fill="#a855f766" fontSize={7} fontFamily="monospace">±800</text>
        <line x1={W*0.02} y1={CY_ep-5*scaleRW} x2={W*0.02} y2={CY_ep+5*scaleRW}
          stroke="#22c55e33" strokeWidth={3}/>
        <text x={0} y={CY_ep+5*scaleRW+8} fill="#22c55e66" fontSize={7} fontFamily="monospace">±5</text>
        <path d={toPath(rwTrace, CY_rw, scaleRW)} fill="none" stroke="#a855f7" strokeWidth={1.2} opacity={0.85}/>
        <path d={toPath(epTrace, CY_ep, scaleRW*1.2)} fill="none" stroke="#22c55e" strokeWidth={1.2} opacity={0.85}/>
        <text x={W*0.78} y={CY_rw+20} fill="#ef4444" fontSize={8} fontFamily="monospace">→ 360m smear</text>
        <text x={W*0.78} y={CY_ep+20} fill="#22c55e" fontSize={8} fontFamily="monospace">→ 2.25m smear</text>
        <text x={W*0.97} y={CY_rw+3} fill="#3a5068" fontSize={7} fontFamily="monospace">0</text>
        <text x={W*0.97} y={CY_ep+3} fill="#3a5068" fontSize={7} fontFamily="monospace">0</text>
      </svg>
      <div style={{...mono,fontSize:9,color:C.muted,textAlign:"center",marginTop:4}}>
        At 450km altitude · RW bearing harmonics visible at 8, 12, 35, 60 Hz ·
        EP-ADCS noise floor is thermal + electronics only
      </div>
    </div>
  );
}

// ── STATION KEEPING CYCLE COMPARISON ─────────────────────────
function CycleCompareViz({ D }) {
  const W=560, H=160;
  const barH=28, gap=18;
  const y_ion=30, y_ep=y_ion+barH+gap+20;
  const totalT=W*0.88;
  const x0=W*0.11;
  const ionScale=totalT/1800;

  return (
    <div style={{background:"#030810",borderRadius:8,padding:14,marginBottom:14}}>
      <SL color="#f59e0b">Station Keeping Duty Cycle — 30 Minutes</SL>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`}>
        <line x1={x0} y1={H-20} x2={x0+totalT} y2={H-20} stroke="#0e2035" strokeWidth={1}/>
        {[0,5,10,15,20,25,30].map(m=>(
          <g key={m}>
            <line x1={x0+m*2*ionScale} y1={H-22} x2={x0+m*2*ionScale} y2={H-18} stroke="#0e2035" strokeWidth={1}/>
            <text x={x0+m*2*ionScale} y={H-10} textAnchor="middle" fill={C.muted} fontSize={7} fontFamily="monospace">{m}min</text>
          </g>
        ))}
        <text x={2} y={y_ion+barH*0.65} fill="#a855f7" fontSize={8} fontFamily="monospace">ION</text>
        {[0,1].map(ci=>{
          const xStart = x0 + ci*900*ionScale;
          const warmW  = 900*0.95*ionScale;
          const fireW  = 900*0.04*ionScale;
          const deadW  = 900*0.01*ionScale;
          return (
            <g key={ci}>
              <rect x={xStart} y={y_ion} width={warmW} height={barH}
                fill="#a855f711" stroke="#a855f733" strokeWidth={0.5} rx={2}/>
              <text x={xStart+warmW/2} y={y_ion+barH*0.6} textAnchor="middle"
                fill="#a855f766" fontSize={7} fontFamily="monospace">WARM-UP 15min</text>
              <rect x={xStart+warmW} y={y_ion} width={fireW} height={barH} fill="#a855f7" rx={1}/>
              <text x={xStart+warmW+fireW/2} y={y_ion+barH*0.65} textAnchor="middle"
                fill="#fff" fontSize={6} fontFamily="monospace">FIRE</text>
              <rect x={xStart+warmW+fireW} y={y_ion+4} width={deadW*2} height={barH-8}
                fill="#ef444433" stroke="#ef444455" strokeWidth={0.5} rx={1}/>
            </g>
          );
        })}
        <text x={x0+totalT*0.92} y={y_ion+barH*0.65} fill="#ef4444" fontSize={7} fontFamily="monospace">
          drift: ~{f(D.Fd*900/D.satMass,1)}m</text>
        <text x={0} y={y_ep+barH*0.65} fill="#22c55e" fontSize={7} fontFamily="monospace">EP-ADCS</text>
        {Array.from({length:13},(_,ci)=>{
          const xStart = x0 + ci*133*ionScale;
          if(xStart > x0+totalT) return null;
          const totalW = 133*ionScale;
          const fireW  = 0.005*133*ionScale;
          const absW   = 0.0096*133*ionScale;
          const resetW = 0.0096*133*ionScale;
          const idleW  = totalW-fireW-absW-resetW;
          return (
            <g key={ci}>
              <rect x={xStart} y={y_ep} width={idleW} height={barH}
                fill="#22c55e08" stroke="#22c55e22" strokeWidth={0.5} rx={1}/>
              <rect x={xStart+idleW} y={y_ep} width={fireW+1} height={barH} fill="#22c55e" rx={1}/>
              <rect x={xStart+idleW+fireW} y={y_ep+2} width={absW} height={barH-4} fill="#0e9cc066" rx={1}/>
              <rect x={xStart+idleW+fireW+absW} y={y_ep+4} width={resetW} height={barH-8} fill="#0e9cc033" rx={1}/>
            </g>
          );
        })}
        <text x={x0+totalT*0.92} y={y_ep+barH*0.65} fill="#22c55e" fontSize={7} fontFamily="monospace">
          drift: ~{f(D.Fd*133/D.satMass,2)}m</text>
        {[
          {c:"#a855f711",bc:"#a855f733",l:"Warm-up (wasted time)"},
          {c:"#a855f7",bc:"#a855f7",l:"Ion firing"},
          {c:"#22c55e08",bc:"#22c55e22",l:"EP-ADCS idle"},
          {c:"#22c55e",bc:"#22c55e",l:"EP-ADCS fire+absorb"},
        ].map((it,i)=>(
          <g key={i}>
            <rect x={x0+i*135} y={H-8} width={10} height={7} fill={it.c} stroke={it.bc} strokeWidth={0.5} rx={1}/>
            <text x={x0+i*135+13} y={H-2} fill={C.muted} fontSize={7} fontFamily="monospace">{it.l}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

// ── ATTITUDE RESPONSE VISUALISATION ──────────────────────────
function AttitudeResponseViz() {
  const W=500, H=170;
  const [running, setRunning] = useState(false);
  const raf = useRef(null);
  const tRef = useRef(0);
  const [, setT] = useState(0);

  useEffect(() => {
    if(running){
      const step=()=>{tRef.current+=0.06; setT(tRef.current); raf.current=requestAnimationFrame(step);};
      raf.current=requestAnimationFrame(step);
    } else cancelAnimationFrame(raf.current);
    return()=>cancelAnimationFrame(raf.current);
  },[running]);

  const x0=50, plotW=W-60, plotH=H-40;
  const xScale = plotW/15;
  const yScale = plotH/1.5;

  const epResponse = (time) => {
    if(time<0) return 0;
    const x = time/1.1;
    return 1 - Math.exp(-3*x) * (Math.cos(2.5*x) + 0.03*Math.sin(2.5*x));
  };

  const rwResponse = (time) => {
    if(time<0) return 0;
    if(time<300) return (time/300)*0.4 * (1-Math.exp(-time/60));
    const t2=time-300;
    return 0.4 + 0.6*(1-Math.exp(-t2/3))*(1+0.12*Math.exp(-t2/1.5)*Math.cos(1.5*t2));
  };

  const pts=200;
  const epPts=Array.from({length:pts},(_,i)=>{
    const tt=(i/pts)*15;
    const v=epResponse(tt-0.1);
    return `${x0+tt*xScale},${30+plotH-Math.min(Math.max(v,0),1.3)*yScale}`;
  }).join(" ");
  const rwPts=Array.from({length:pts},(_,i)=>{
    const tt=(i/pts)*15;
    const v=rwResponse(tt-0.1);
    return `${x0+tt*xScale},${30+plotH-Math.min(Math.max(v,0),1.3)*yScale}`;
  }).join(" ");

  return (
    <div style={{background:"#030810",borderRadius:8,padding:14,marginBottom:14}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <SL color="#6366f1">Attitude Step Response — 5° Slew Command</SL>
        <button onClick={()=>setRunning(r=>!r)} style={{
          padding:"4px 12px",...mono,fontSize:9,cursor:"pointer",
          background:running?"#ef444420":"#6366f120",
          border:`1px solid ${running?"#ef444455":"#6366f155"}`,
          color:running?"#ef4444":"#6366f1",borderRadius:3}}>
          {running?"■ STOP":"▶ ANIMATE"}</button>
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`}>
        {[0,0.25,0.5,0.75,1.0,1.25].map(v=>(
          <g key={v}>
            <line x1={x0} y1={30+plotH-v*yScale} x2={x0+plotW} y2={30+plotH-v*yScale}
              stroke="#0e203544" strokeWidth={0.5}/>
            <text x={x0-4} y={30+plotH-v*yScale+3} fill={C.muted} fontSize={6}
              fontFamily="monospace" textAnchor="end">{(v*100).toFixed(0)}%</text>
          </g>
        ))}
        {[0,2,4,6,8,10,12,14].map(s=>(
          <g key={s}>
            <line x1={x0+s*xScale} y1={30} x2={x0+s*xScale} y2={30+plotH}
              stroke="#0e203533" strokeWidth={0.5}/>
            <text x={x0+s*xScale} y={30+plotH+12} textAnchor="middle"
              fill={C.muted} fontSize={6} fontFamily="monospace">{s}s</text>
          </g>
        ))}
        <line x1={x0} y1={30+plotH-yScale} x2={x0+plotW} y2={30+plotH-yScale}
          stroke="#0e9cc033" strokeWidth={1} strokeDasharray="4 3"/>
        <text x={x0+plotW+2} y={30+plotH-yScale+3} fill="#0e9cc066" fontSize={7} fontFamily="monospace">target</text>
        <polyline points={rwPts} fill="none" stroke="#a855f7" strokeWidth={1.5} opacity={0.8}/>
        <polyline points={epPts} fill="none" stroke="#22c55e" strokeWidth={1.5} opacity={0.8}/>
        <line x1={x0+1.1*xScale} y1={30} x2={x0+1.1*xScale} y2={30+plotH}
          stroke="#22c55e44" strokeWidth={1} strokeDasharray="2 2"/>
        <text x={x0+1.1*xScale+2} y={42} fill="#22c55e" fontSize={7} fontFamily="monospace">EP settle 1.1s</text>
        <line x1={x0+8.5*xScale} y1={30} x2={x0+8.5*xScale} y2={30+plotH}
          stroke="#a855f744" strokeWidth={1} strokeDasharray="2 2"/>
        <text x={x0+8.5*xScale+2} y={55} fill="#a855f7" fontSize={7} fontFamily="monospace">RW settle ~8.5s</text>
        <text x={x0+plotW/2} y={H-2} textAnchor="middle" fill={C.muted} fontSize={8} fontFamily="monospace">Time (seconds)</text>
        <text x={12} y={30+plotH/2} fill={C.muted} fontSize={8} fontFamily="monospace"
          transform={`rotate(-90,12,${30+plotH/2})`}>Slew progress</text>
        <line x1={x0+20} y1={H-28} x2={x0+40} y2={H-28} stroke="#22c55e" strokeWidth={1.5}/>
        <text x={x0+44} y={H-24} fill="#22c55e" fontSize={8} fontFamily="monospace">
          EP-ADCS EMMS (settle 1.1s, overshoot 3%)</text>
        <line x1={x0+230} y1={H-28} x2={x0+250} y2={H-28} stroke="#a855f7" strokeWidth={1.5}/>
        <text x={x0+254} y={H-24} fill="#a855f7" fontSize={8} fontFamily="monospace">
          Reaction Wheel (settle 8.5s, overshoot 12%)</text>
      </svg>
    </div>
  );
}

// ── POWER BUDGET VISUALISATION ────────────────────────────────
function PowerBudgetViz({ D }) {
  const W=500, H=160;
  const items = [
    {label:"Thruster/EMMS active",   ion:D.ionPow, ep:0.8,   unit:"W"},
    {label:"Attitude control (hold)", ion:12,       ep:0.004, unit:"W"},
    {label:"Power processing",        ion:15,       ep:0.2,   unit:"W"},
    {label:"Housekeeping bus",        ion:8,        ep:8,     unit:"W"},
  ];
  const maxW = D.ionPow+15+12+8;
  const barH=18, gap=10, x0=180, barMaxW=W-x0-30;

  return (
    <div style={{background:"#030810",borderRadius:8,padding:14,marginBottom:14}}>
      <SL color="#f59e0b">Power Budget — Side by Side</SL>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`}>
        {items.map((item,i)=>{
          const y=15+i*(barH*2+gap);
          const ionW=(item.ion/maxW)*barMaxW;
          const epW=(item.ep/maxW)*barMaxW;
          return (
            <g key={item.label}>
              <text x={0} y={y+barH*0.7} fill={C.muted} fontSize={8} fontFamily="monospace">{item.label}</text>
              <rect x={x0} y={y} width={Math.max(ionW,1)} height={barH} fill="#a855f777" rx={2}/>
              <text x={x0+Math.max(ionW,1)+4} y={y+barH*0.72} fill="#a855f7" fontSize={8} fontFamily="monospace">
                {item.ion}{item.unit}</text>
              <rect x={x0} y={y+barH+2} width={Math.max(epW,1)} height={barH} fill="#22c55e55" rx={2}/>
              <text x={x0+Math.max(epW,1)+4} y={y+barH+2+barH*0.72} fill="#22c55e" fontSize={8} fontFamily="monospace">
                {item.ep}{item.unit}</text>
            </g>
          );
        })}
        <text x={x0} y={H-8} fill="#a855f7" fontSize={8} fontFamily="monospace">
          Ion total: {D.ionPow+35}W</text>
        <text x={x0+160} y={H-8} fill="#22c55e" fontSize={8} fontFamily="monospace">
          EP-ADCS total: {(8.004+0.8+0.2).toFixed(1)}W</text>
      </svg>
    </div>
  );
}

// ── PERFORMANCE SCORECARD ─────────────────────────────────────
const PERF = [
  {
    cat:"Station Keeping Accuracy",
    metrics:[
      {label:"Max position drift before correction",
       ion:(D)=>`${f(D.Fd*900/D.satMass,1)}m`,
       ep: (D)=>`${f(D.Fd*133/D.satMass,2)}m`,
       winner:"ep",
       note:"EP corrects every 133s. Ion waits 15min warm-up per cycle."},
      {label:"Station keeping box (3σ)",
       ion:(D)=>`±${f(D.Fd*900/D.satMass*1.5,1)}m`,
       ep: (D)=>`±${f(D.Fd*133/D.satMass*0.5,2)}m`,
       winner:"ep",
       note:"Smaller box = better formation flying, better coverage prediction."},
      {label:"Correction cadence",
       ion:()=>"Every ~15 min", ep:()=>"Every 133 sec",
       winner:"ep", note:"Ion limited by xenon warm-up time."},
    ]
  },
  {
    cat:"Attitude Control",
    metrics:[
      {label:"Pointing jitter (RMS)",
       ion:()=>"800 μrad", ep:()=>"< 5 μrad", winner:"ep",
       note:"Ion still requires reaction wheels — jitter source remains."},
      {label:"Attitude settle time (5° slew)",
       ion:()=>"6–9 s", ep:()=>"0.8–1.5 s", winner:"ep",
       note:"EMMS electromagnetic response vs RW spin-up."},
      {label:"Desaturation burns / year",
       ion:()=>"25–50", ep:()=>"Zero", winner:"ep",
       note:"RW saturate from SRP and magnetic torques."},
      {label:"Image smear at 450km",
       ion:()=>"360 m", ep:()=>"2.25 m", winner:"ep",
       note:"160× improvement. Direct impact on imaging resolution."},
    ]
  },
  {
    cat:"Propulsion Performance",
    metrics:[
      {label:"Isp (specific impulse)",
       ion:()=>"1,500 s", ep:(D)=>`${D.ispEP} s`, winner:"ion",
       note:"Ion wins decisively on Isp — this is its core advantage."},
      {label:"Propellant mass (5yr)",
       ion:(D)=>`${f(D.prop_ion,3)} kg`, ep:(D)=>`${f(D.prop_ep,3)} kg`,
       winner:"ion", note:"Ion propellant mass wins by 10–30×."},
      {label:"Overshoot factor η",
       ion:()=>"1.058", ep:()=>"1.020", winner:"ep",
       note:"EP-ADCS closer to theoretical perfect (η=1.000)."},
      {label:"Response time",
       ion:()=>"15 min warm-up", ep:()=>"20 ms", winner:"ep",
       note:"Ion cannot respond to oscillatory perturbations."},
      {label:"Total system mass",
       ion:(D)=>`${f(D.ion.total,2)} kg`, ep:(D)=>`${f(D.ep.total,2)} kg`,
       winner:(D)=>D.ep.total<D.ion.total?"ep":"ion",
       note:"Includes propellant + power hardware + attitude hardware."},
    ]
  },
  {
    cat:"Mission Compatibility",
    metrics:[
      {label:"Optical payload compatible",
       ion:()=>"NO — plume", ep:()=>"YES", winner:"ep",
       note:"Xenon plume deposits on mirrors and coatings."},
      {label:"CubeSat (< 30kg) compatible",
       ion:()=>"NO — 150W", ep:()=>"YES — 0.2W", winner:"ep",
       note:"Ion solar panels alone exceed CubeSat mass budget."},
      {label:"Reaction wheels required",
       ion:()=>"YES — still needed", ep:()=>"NO — eliminated", winner:"ep",
       note:"Ion provides linear thrust only. EMMS replaces wheels entirely."},
      {label:"Deep space / GEO / HEO",
       ion:()=>"YES", ep:()=>"NO", winner:"ion",
       note:"No atmosphere above ~600km. EP-ADCS drag saving = zero."},
    ]
  },
  {
    cat:"Reliability & Lifetime",
    metrics:[
      {label:"Moving parts (attitude)",
       ion:()=>"3× RW bearings", ep:()=>"1× linear track", winner:"ep",
       note:"Bearing failures caused Kepler, Hayabusa attitude loss."},
      {label:"Primary failure mode",
       ion:()=>"PPU + bearing", ep:()=>"Track wear (minor)", winner:"ep",
       note:"Ion PPU failure = total propulsion loss. EMMS failure = minor drift."},
      {label:"Warm-up cycles to end of life",
       ion:()=>`${((5*365*24*60)/15).toFixed(0)} cycles`,
       ep:()=>`${(5*365*649).toFixed(0)} cycles`, winner:"none",
       note:"Ion: 175,000 warm-up cycles. EP-ADCS: 1.18M valve cycles (rated 10M+)."},
    ]
  },
];

// ═══════════════════════════════════════════════════════════
//  MAIN
// ═══════════════════════════════════════════════════════════
export default function IonVsEPADCS() {
  const [satClass, setSatClass] = useState("30kg");
  const [ispEP, setIspEP] = useState(850);  // EP-ADCS baseline
  const [alt, setAlt] = useState(425);
  const [tab, setTab] = useState("visual");
  const [perfCat, setPerfCat] = useState(0);

  const configs = {
    "30kg":  {mass:30,  label:"30 kg Smallsat",  color:"#0e9cc0"},
    "100kg": {mass:100, label:"100 kg Microsat", color:"#f59e0b"},
    "200kg": {mass:200, label:"200 kg Microsat", color:"#f97316"},
  };
  const cfg = configs[satClass];
  const D = { ...physics(cfg.mass, alt, ispEP), ispEP };
  const winner = D.ep.total < D.ion.total ? "ep" : "ion";
  const margin  = Math.abs(D.ion.total - D.ep.total);

  const tabs = [
    {id:"visual",  label:"① Visual Simulations"},
    {id:"perf",    label:"② Performance"},
    {id:"mass",    label:"③ System Mass"},
    {id:"apps",    label:"④ Applications"},
  ];

  return (
    <div style={{color:C.text,fontFamily:"monospace"}}>

      <p style={{...mono,fontSize:11,color:C.muted,margin:"0 0 24px",lineHeight:1.8}}>
        Hardware mass · Station keeping performance · Attitude control · Jitter · Power · Applications
      </p>

      {/* Config */}
      <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:16,marginBottom:20}}>
        <SL>Mission Configuration</SL>
        <div style={{display:"flex",gap:20,flexWrap:"wrap"}}>
          <div>
            <div style={{...mono,fontSize:9,color:C.muted,marginBottom:6}}>SATELLITE CLASS</div>
            <div style={{display:"flex",gap:4}}>
              {Object.entries(configs).map(([k,v])=>(
                <button key={k} onClick={()=>setSatClass(k)} style={{
                  padding:"5px 12px",...mono,fontSize:10,cursor:"pointer",
                  background:satClass===k?`${v.color}20`:"transparent",
                  border:`1px solid ${satClass===k?v.color+"66":C.border}`,
                  color:satClass===k?v.color:C.muted,borderRadius:4}}>{v.label}</button>
              ))}
            </div>
          </div>
          <div>
            <div style={{...mono,fontSize:9,color:C.muted,marginBottom:6}}>EP-ADCS ISP</div>
            <div style={{display:"flex",gap:4}}>
              {[{v:70,l:"Cold 70s"},{v:150,l:"Warm 150s"},{v:300,l:"Mono 300s"},{v:850,l:"EP 850s ★"}].map(i=>(
                <button key={i.v} onClick={()=>setIspEP(i.v)} style={{
                  padding:"5px 11px",...mono,fontSize:10,cursor:"pointer",
                  background:ispEP===i.v?"#22c55e20":"transparent",
                  border:`1px solid ${ispEP===i.v?"#22c55e66":C.border}`,
                  color:ispEP===i.v?"#22c55e":C.muted,borderRadius:4}}>{i.l}</button>
              ))}
            </div>
          </div>
          <div>
            <div style={{...mono,fontSize:9,color:C.muted,marginBottom:6}}>ALTITUDE</div>
            <div style={{display:"flex",gap:4}}>
              {[400,425,450,500].map(a=>(
                <button key={a} onClick={()=>setAlt(a)} style={{
                  padding:"5px 10px",...mono,fontSize:10,cursor:"pointer",
                  background:alt===a?"#f59e0b20":"transparent",
                  border:`1px solid ${alt===a?"#f59e0b66":C.border}`,
                  color:alt===a?"#f59e0b":C.muted,borderRadius:4}}>{a}km</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Top KPIs */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8,marginBottom:20}}>
        {[
          {l:"Ion system mass",    v:f(D.ion.total), u:"kg",   c:"#a855f7"},
          {l:"EP-ADCS system mass",v:f(D.ep.total),  u:"kg",   c:"#22c55e"},
          {l:winner==="ep"?"EP-ADCS saves":"Ion saves", v:f(margin), u:"kg", c:winner==="ep"?"#0e9cc0":"#a855f7"},
          {l:"Jitter: ion RW",    v:"800",  u:"μrad", c:"#ef4444"},
          {l:"Jitter: EP-ADCS",  v:"< 5",  u:"μrad", c:"#22c55e"},
        ].map(s=>(
          <div key={s.l} style={{background:C.panel,
            border:`1px solid ${s.c}22`,borderTop:`2px solid ${s.c}66`,
            borderRadius:8,padding:"10px 12px"}}>
            <div style={{...mono,fontSize:8,color:C.muted,letterSpacing:2,marginBottom:4}}>{s.l}</div>
            <div style={{...mono,fontSize:20,fontWeight:700,color:s.c}}>
              {s.v}<span style={{fontSize:10,color:C.muted,marginLeft:3}}>{s.u}</span></div>
          </div>
        ))}
      </div>

      {/* Tab bar */}
      <div style={{display:"flex",gap:4,marginBottom:20}}>
        {tabs.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{
            flex:1,padding:"8px 4px",...mono,fontSize:10,cursor:"pointer",
            background:tab===t.id?"#0e9cc018":"transparent",
            border:`1px solid ${tab===t.id?"#0e9cc066":C.border}`,
            color:tab===t.id?"#0e9cc0":C.muted,borderRadius:4}}>
            {t.label}</button>
        ))}
      </div>

      {/* ── TAB: VISUAL SIMULATIONS ── */}
      {tab==="visual" && (
        <>
          <OrbitDriftViz D={D} />
          <CycleCompareViz D={D} />
          <JitterViz />
          <AttitudeResponseViz />
          <PowerBudgetViz D={D} />
        </>
      )}

      {/* ── TAB: PERFORMANCE ── */}
      {tab==="perf" && (
        <>
          <div style={{display:"flex",gap:4,marginBottom:16,flexWrap:"wrap"}}>
            {PERF.map((c,i)=>(
              <button key={i} onClick={()=>setPerfCat(i)} style={{
                padding:"6px 12px",...mono,fontSize:9,cursor:"pointer",
                background:perfCat===i?"#0e9cc018":"transparent",
                border:`1px solid ${perfCat===i?"#0e9cc066":C.border}`,
                color:perfCat===i?"#0e9cc0":C.muted,borderRadius:4}}>
                {c.cat}</button>
            ))}
          </div>

          <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:18}}>
            <SL color="#0e9cc0">{PERF[perfCat].cat}</SL>
            <div style={{display:"grid",gridTemplateColumns:"1fr 160px 160px 80px",
              gap:8,paddingBottom:8,borderBottom:`1px solid ${C.border}`,marginBottom:4}}>
              {["METRIC","ION THRUSTER","EP-ADCS","WINNER"].map((h,i)=>(
                <div key={h} style={{...mono,fontSize:9,
                  color:i===1?"#a855f7":i===2?"#22c55e":C.muted}}>{h}</div>
              ))}
            </div>
            {PERF[perfCat].metrics.map((m,i)=>{
              const ionVal = typeof m.ion==="function" ? m.ion(D) : m.ion;
              const epVal  = typeof m.ep==="function"  ? m.ep(D)  : m.ep;
              const w      = typeof m.winner==="function" ? m.winner(D) : m.winner;
              return (
                <div key={m.label} style={{
                  display:"grid",gridTemplateColumns:"1fr 160px 160px 80px",
                  gap:8,padding:"10px 0",
                  borderBottom:`1px solid ${C.border}22`,
                  background:i%2===0?"#ffffff02":"transparent"}}>
                  <div>
                    <div style={{...mono,fontSize:11,color:C.text}}>{m.label}</div>
                    <div style={{...mono,fontSize:9,color:C.muted,marginTop:2,lineHeight:1.5}}>{m.note}</div>
                  </div>
                  <div style={{...mono,fontSize:12,fontWeight:700,
                    color:w==="ion"?"#22c55e":"#a855f7"}}>{ionVal}</div>
                  <div style={{...mono,fontSize:12,fontWeight:700,
                    color:w==="ep"?"#22c55e":"#a855f7"}}>{epVal}</div>
                  <div style={{...mono,fontSize:10,fontWeight:700,
                    color:w==="ep"?"#22c55e":w==="ion"?"#a855f7":"#f59e0b"}}>
                    {w==="ep"?"EP ✓":w==="ion"?"Ion ✓":"Tied"}
                  </div>
                </div>
              );
            })}
          </div>

          {(() => {
            let epScore=0, ionScore=0;
            PERF.forEach(cat=>cat.metrics.forEach(m=>{
              const w=typeof m.winner==="function"?m.winner(D):m.winner;
              if(w==="ep") epScore++;
              else if(w==="ion") ionScore++;
            }));
            return (
              <div style={{background:C.panel,border:`1px solid ${C.border}`,
                borderRadius:8,padding:18,marginTop:14}}>
                <SL>Overall Performance Score (all categories)</SL>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                  {[
                    {label:"EP-ADCS wins",score:epScore,color:"#22c55e"},
                    {label:"Ion wins",score:ionScore,color:"#a855f7"},
                  ].map(s=>(
                    <div key={s.label}>
                      <div style={{...mono,fontSize:11,color:s.color,marginBottom:6}}>
                        {s.label}: {s.score}/{epScore+ionScore} metrics</div>
                      <div style={{height:12,background:"#030810",borderRadius:6,overflow:"hidden"}}>
                        <div style={{height:"100%",
                          width:`${(s.score/(epScore+ionScore))*100}%`,
                          background:s.color,borderRadius:6}}/>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{...mono,fontSize:10,color:C.muted,marginTop:12,lineHeight:1.7}}>
                  EP-ADCS wins on: station keeping accuracy, attitude control, jitter,
                  response time, power, CubeSat compatibility, optical payloads, reliability.<br/>
                  Ion wins on: raw Isp, propellant mass, deep space / GEO missions.
                </div>
              </div>
            );
          })()}
        </>
      )}

      {/* ── TAB: MASS ── */}
      {tab==="mass" && (
        <>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
            {[
              {title:"Ion Thruster System", data:D.ion, color:"#a855f7",
                items:[
                  {k:"propellant",l:"Propellant (Xe)",sub:"Isp=1500s, η=1.058"},
                  {k:"ppu",l:"Power Processing Unit",sub:"heaviest item"},
                  {k:"solar",l:`Solar panels (${D.ionPow}W)`,sub:"ion power req"},
                  {k:"battery",l:"Battery (eclipse)",sub:"stores ion power"},
                  {k:"thruster",l:"Thruster unit"},
                  {k:"rw",l:"Reaction wheels",sub:"still required"},
                  {k:"tank",l:"Tank & feed system"},
                  {k:"pcu",l:"Power conditioning"},
                ]},
              {title:"EP-ADCS System", data:D.ep, color:"#22c55e",
                items:[
                  {k:"propellant",l:"Propellant",sub:`Isp=${ispEP}s, η=1.020`},
                  {k:"emms",l:"EMMS (4-axis)",sub:"replaces RW"},
                  {k:"thruster",l:"Thruster unit"},
                  {k:"tank",l:"Tank & feed"},
                  {k:"emmsCtrl",l:"EMMS controller"},
                  {k:"solar",l:"Solar panels (0.5W)",sub:"minimal"},
                  {k:"battery",l:"Battery"},
                  {k:"pcu",l:"PCU"},
                ]},
            ].map(side=>{
              const max=Math.max(D.ion.total,D.ep.total)*1.05;
              return (
                <div key={side.title} style={{background:C.panel,
                  border:`1px solid ${side.color}22`,
                  borderTop:`2px solid ${side.color}66`,
                  borderRadius:8,padding:16}}>
                  <SL color={side.color}>{side.title}</SL>
                  {side.items.map(item=>{
                    const val=side.data[item.k]||0;
                    const w=(val/max)*100;
                    return (
                      <div key={item.k} style={{marginBottom:7}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                          <span style={{...mono,fontSize:10,color:C.text}}>{item.l}
                            {item.sub&&<span style={{color:C.muted,fontSize:8}}> · {item.sub}</span>}
                          </span>
                          <span style={{...mono,fontSize:10,color:side.color,fontWeight:700}}>
                            {f(val,3)} kg</span>
                        </div>
                        <div style={{height:6,background:"#030810",borderRadius:3,overflow:"hidden"}}>
                          <div style={{height:"100%",width:`${w}%`,
                            background:side.color,opacity:0.7,borderRadius:3}}/>
                        </div>
                      </div>
                    );
                  })}
                  <div style={{borderTop:`1px solid ${C.border}`,marginTop:10,
                    paddingTop:8,display:"flex",justifyContent:"space-between"}}>
                    <span style={{...mono,fontSize:11,color:C.muted}}>TOTAL</span>
                    <span style={{...mono,fontSize:20,color:side.color,fontWeight:700}}>
                      {f(side.data.total,2)} kg</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:16}}>
            <SL>Crossover Analysis — Full System Mass by Satellite Mass</SL>
            <div style={{display:"grid",
              gridTemplateColumns:"70px 120px 120px 100px 80px 1fr",
              gap:8,paddingBottom:8,borderBottom:`1px solid ${C.border}`,marginBottom:4}}>
              {["SAT MASS","ION SYSTEM","EP-ADCS","DIFFERENCE","WINNER","NOTE"].map(h=>(
                <div key={h} style={{...mono,fontSize:8,color:C.muted}}>{h}</div>
              ))}
            </div>
            {[8,14,30,50,100,200,350,500].map(m=>{
              const d=physics(m,alt,ispEP);
              const w=d.ep.total<d.ion.total;
              const diff=Math.abs(d.ion.total-d.ep.total);
              return (
                <div key={m} style={{
                  display:"grid",
                  gridTemplateColumns:"70px 120px 120px 100px 80px 1fr",
                  gap:8,padding:"6px 0",
                  borderBottom:`1px solid ${C.border}22`,
                  background:m===cfg.mass?"#0e9cc008":"transparent"}}>
                  <div style={{...mono,fontSize:10,
                    color:m===cfg.mass?"#0e9cc0":C.text,
                    fontWeight:m===cfg.mass?"700":"400"}}>{m} kg</div>
                  <div style={{...mono,fontSize:10,color:"#a855f7"}}>{f(d.ion.total,1)} kg</div>
                  <div style={{...mono,fontSize:10,color:"#22c55e"}}>{f(d.ep.total,1)} kg</div>
                  <div style={{...mono,fontSize:10,color:C.muted}}>{f(diff,1)} kg</div>
                  <div style={{...mono,fontSize:10,fontWeight:700,
                    color:w?"#22c55e":"#a855f7"}}>{w?"EP ✓":"Ion ✓"}</div>
                  <div style={{...mono,fontSize:9,color:C.muted}}>
                    {w?"Ion solar+PPU penalty > prop saving":"Ion Isp saves enough"}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── TAB: APPLICATIONS ── */}
      {tab==="apps" && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          {[
            {icon:"📷",title:"Optical Imaging",range:"5–200 kg",alt:"300–500 km",
             verdict:"EP-ADCS only",color:"#22c55e",
             ep:["< 5μrad jitter → 2.25m smear vs 360m RW",
               "Zero plume contamination on optics",
               "Drag comp enables lower altitude → better GSD"],
             ion:["Xenon plume deposits on mirrors","RW jitter degrades resolution","Hard disqualification"]},
            {icon:"🛰",title:"CubeSat (< 30kg)",range:"1–30 kg",alt:"400–600 km",
             verdict:"EP-ADCS only",color:"#0e9cc0",
             ep:["0.22W total — fits any CubeSat power budget",
               "System mass wins by >50% of sat mass",
               "All savings mechanisms active"],
             ion:["150W > entire 6U power budget","Solar panels > total sat mass","Physically impossible"]},
            {icon:"🔴",title:"Laser ISL",range:"20–150 kg",alt:"400–600 km",
             verdict:"EP-ADCS preferred",color:"#f59e0b",
             ep:["EMMS eliminates RW jitter at FSM frequencies",
               "No plume risk near laser optics",
               "48% propellant saving → longer mission"],
             ion:["RW still needed → 800μrad jitter remains","Plume risk near laser terminals","Soft disqualification"]},
            {icon:"✦",title:"Formation Flying",range:"3–100 kg",alt:"400–600 km",
             verdict:"EP-ADCS uniquely capable",color:"#6366f1",
             ep:["0.2s attitude transition → sub-km drag phasing",
               "Zero propellant formation keeping",
               "Day-night density sync → 10× faster phasing"],
             ion:["5–10min RW transition → misses density windows","Cannot exploit differential drag","Partial disqualification"]},
            {icon:"⚖",title:"Medium Sat 30–350kg",range:"30–350 kg",alt:"400–600 km",
             verdict:"Case by case",color:"#f97316",
             ep:["EP-ADCS wins full system mass below ~350kg",
               "Isp=300s green monoprop closes the gap",
               "Optical payload always favours EP-ADCS"],
             ion:["Propellant mass advantage grows with sat mass","Pure comms > 150kg: ion viable","Run full system mass calculation"]},
            {icon:"🚀",title:"Large Sat / GEO / Deep Space",range:"> 350 kg",alt:"GEO / HEO",
             verdict:"Ion wins",color:"#a855f7",
             ep:["EMMS attitude control still valid for jitter",
               "Overshoot saving on large planned burns (~5%)",
               "No other EP-ADCS advantage"],
             ion:["Isp advantage finally outweighs power penalty","No atmosphere → no drag saving needed","Correct choice for propulsion"]},
          ].map(app=>(
            <div key={app.title} style={{background:C.panel,
              border:`1px solid ${app.color}22`,
              borderTop:`2px solid ${app.color}66`,
              borderRadius:8,padding:16}}>
              <div style={{display:"flex",justifyContent:"space-between",
                alignItems:"flex-start",marginBottom:10}}>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <span style={{fontSize:22}}>{app.icon}</span>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:app.color}}>{app.title}</div>
                    <div style={{...mono,fontSize:8,color:C.muted,marginTop:1}}>
                      {app.range} · {app.alt}</div>
                  </div>
                </div>
                <div style={{...mono,fontSize:8,color:app.color,
                  background:`${app.color}15`,border:`1px solid ${app.color}33`,
                  borderRadius:3,padding:"3px 7px",textAlign:"right",maxWidth:110}}>
                  {app.verdict}</div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                <div>
                  <div style={{...mono,fontSize:8,color:"#22c55e",marginBottom:6,letterSpacing:2}}>EP-ADCS ✓</div>
                  {app.ep.map((p,i)=>(
                    <div key={i} style={{display:"flex",gap:6,marginBottom:4}}>
                      <span style={{color:"#22c55e",fontSize:8}}>→</span>
                      <span style={{...mono,fontSize:9,color:C.muted,lineHeight:1.5}}>{p}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <div style={{...mono,fontSize:8,color:"#ef4444",marginBottom:6,letterSpacing:2}}>ION ISSUES</div>
                  {app.ion.map((p,i)=>(
                    <div key={i} style={{display:"flex",gap:6,marginBottom:4}}>
                      <span style={{color:"#ef4444",fontSize:8}}>✗</span>
                      <span style={{...mono,fontSize:9,color:C.muted,lineHeight:1.5}}>{p}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}
